/**
 * The outbox: everything the user changed on this device that the server has not confirmed yet.
 *
 * Recording, editing or voiding a transaction does two things in ONE atomic commit: it changes
 * the device's own copy (so the app reflects it instantly, online or not) and it appends an
 * operation to the outbox. The sync engine later sends the operations to the server, which
 * remains the authority: whatever it answers is what the device then adopts.
 *
 * Rules that keep this safe (each one is tested):
 *  - Every operation carries an `op_id` and a client-chosen entity id, so a resend after a lost
 *    response can never apply twice (the server remembers `op_id`, and the entity id).
 *  - Edits to an operation that has not been sent merge into it; edits to one that is in flight
 *    become a new operation, re-based on the version the first one produces.
 *  - A server copy never overwrites a transaction that has unsent local edits.
 *  - When the server refuses an operation, the device adopts the server's copy of that
 *    transaction (or drops it, if the server has none) and keeps the operation, with the reason,
 *    for the user to review. Nothing is lost silently and nothing stays half-applied.
 *  - All of it runs one step at a time (`serial`), because "read the queue, then decide" must not
 *    interleave with the engine marking those same operations as sent.
 */
import { ApiError } from "@/lib/api";
import { addMoney, fromUnits, MoneyParseError, subtractMoney, toUnits } from "./decimal";
import { involvedEffects, normaliseName, upsert } from "./repo";
import type { Row, SqlDriver, Statement } from "./types";

export type OutboxAction = "create" | "update" | "void";
export type OutboxStatus = "pending" | "sending" | "acked" | "conflict" | "rejected";

export interface OutboxOp {
  seq: number;
  op_id: string;
  action: OutboxAction;
  entity_id: string;
  base_version: number | null;
  payload: Record<string, unknown>;
  /** This operation's exact effect on account balances: {accountId: signed decimal}. */
  effects: Record<string, string>;
  status: OutboxStatus;
  acked_head: number | null;
  error_code: string | null;
  error_message: string | null;
  error_fields: Record<string, string>;
  /** The server's copy of the transaction when it refused this operation. */
  server: Record<string, unknown> | null;
  created_at: string;
}

/** What the transaction form collects. Money is a decimal string. */
export interface TransactionInput {
  transaction_type: "income" | "expense" | "transfer" | "refund" | "adjustment";
  transaction_date: string;
  amount: string;
  account_id: string;
  counter_account_id?: string | null;
  category_id?: string | null;
  merchant_name?: string | null;
  description?: string;
  reference?: string | null;
  notes?: string | null;
  original_transaction_id?: string | null;
  adjustment_direction?: "increase" | "decrease" | null;
}

export type TransactionChanges = Partial<Omit<TransactionInput, "transaction_type">>;

export interface PushResult {
  index: number;
  op_id: string | null;
  status: "applied" | "already_applied" | "conflict" | "rejected";
  id: string | null;
  version: number | null;
  error: { code: string; message: string; fields?: Record<string, string> } | null;
  server: Record<string, unknown> | null;
}

export interface PushResponse {
  results: PushResult[];
  head: number;
}

/** A protocol operation, exactly as POST /sync/push wants it. */
export type WireOp = Record<string, unknown>;

// --- one step at a time ----------------------------------------------------------------------

const queues = new WeakMap<SqlDriver, Promise<unknown>>();

/** Runs `fn` after everything queued before it on this database has finished (success or not). */
function serial<T>(driver: SqlDriver, fn: () => Promise<T>): Promise<T> {
  const previous = queues.get(driver) ?? Promise.resolve();
  const run = previous.then(fn, fn);
  queues.set(driver, run.catch(() => undefined));
  return run;
}

// --- helpers ---------------------------------------------------------------------------------

const str = (v: unknown): string => String(v ?? "");
const strOrNull = (v: unknown): string | null => (v === null || v === undefined ? null : String(v));
const nowIso = (): string => new Date().toISOString();

function toOp(row: Row): OutboxOp {
  return {
    seq: Number(row.seq),
    op_id: str(row.op_id),
    action: str(row.action) as OutboxAction,
    entity_id: str(row.entity_id),
    base_version: row.base_version === null ? null : Number(row.base_version),
    payload: JSON.parse(str(row.payload)) as Record<string, unknown>,
    effects: JSON.parse(str(row.effects)) as Record<string, string>,
    status: str(row.status) as OutboxStatus,
    acked_head: row.acked_head === null ? null : Number(row.acked_head),
    error_code: strOrNull(row.error_code),
    error_message: strOrNull(row.error_message),
    error_fields: row.error_fields ? (JSON.parse(str(row.error_fields)) as Record<string, string>) : {},
    server: row.server ? (JSON.parse(str(row.server)) as Record<string, unknown>) : null,
    created_at: str(row.created_at),
  };
}

/** Per-account sum of two effect maps; accounts that net to zero are dropped. */
function sumEffects(a: Record<string, string>, b: Record<string, string>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const id of new Set([...Object.keys(a), ...Object.keys(b)])) {
    const total = addMoney(a[id] ?? "0", b[id] ?? "0");
    if (toUnits(total) !== 0n) out[id] = total;
  }
  return out;
}

/** What changes for each account when a transaction goes from `before` to `after`. */
function effectDelta(before: Record<string, string>, after: Record<string, string>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const id of new Set([...Object.keys(before), ...Object.keys(after)])) {
    const change = subtractMoney(after[id] ?? "0", before[id] ?? "0");
    if (toUnits(change) !== 0n) out[id] = change;
  }
  return out;
}

function invalid(field: string, message: string): ApiError {
  return new ApiError(422, "VALIDATION_ERROR", message, { fields: { [field]: message } });
}

function normaliseAmount(amount: string): string {
  try {
    const units = toUnits(amount.replace(/,/g, "").trim());
    if (units <= 0n) throw invalid("amount", "Enter a positive amount");
    return fromUnits(units);
  } catch (error) {
    if (error instanceof MoneyParseError) throw invalid("amount", "Enter a valid amount");
    throw error;
  }
}

/** The local copy of a transaction, with the columns the outbox logic needs. */
interface TxnRow {
  id: string;
  transaction_type: string;
  transaction_date: string;
  amount: string;
  currency: string;
  account_id: string;
  counter_account_id: string | null;
  category_id: string | null;
  adjustment_direction: string | null;
  merchant_id: string | null;
  merchant_name: string | null;
  description: string;
  reference: string | null;
  notes: string | null;
  original_transaction_id: string | null;
  voided_at: string | null;
  void_reason: string | null;
  version: number;
}

const rowToTxn = (r: Row): TxnRow => ({
  id: str(r.id),
  transaction_type: str(r.transaction_type),
  transaction_date: str(r.transaction_date),
  amount: str(r.amount),
  currency: str(r.currency),
  account_id: str(r.account_id),
  counter_account_id: strOrNull(r.counter_account_id),
  category_id: strOrNull(r.category_id),
  adjustment_direction: strOrNull(r.adjustment_direction),
  merchant_id: strOrNull(r.merchant_id),
  merchant_name: strOrNull(r.merchant_name),
  description: str(r.description),
  reference: strOrNull(r.reference),
  notes: strOrNull(r.notes),
  original_transaction_id: strOrNull(r.original_transaction_id),
  voided_at: strOrNull(r.voided_at),
  void_reason: strOrNull(r.void_reason),
  version: Number(r.version),
});

async function loadTxn(driver: SqlDriver, id: string): Promise<TxnRow | null> {
  const rows = await driver.query(`SELECT * FROM transactions WHERE id = ?`, [id]);
  return rows[0] ? rowToTxn(rows[0]) : null;
}

async function accountCurrency(driver: SqlDriver, id: string, field: string): Promise<string> {
  const rows = await driver.query<{ currency: string }>(
    `SELECT currency FROM accounts WHERE id = ? AND deleted_at IS NULL`,
    [id],
  );
  if (!rows[0]) throw invalid(field, "That account is not available");
  return rows[0].currency;
}

/** A merchant the device already knows keeps its id; a new name waits for the server to create it. */
async function resolveMerchant(
  driver: SqlDriver,
  name: string | null | undefined,
): Promise<{ id: string | null; name: string | null }> {
  const trimmed = name?.trim();
  if (!trimmed) return { id: null, name: null };
  const rows = await driver.query<{ id: string }>(
    `SELECT id FROM merchants WHERE deleted_at IS NULL AND normalised_name = ? LIMIT 1`,
    [normaliseName(trimmed)],
  );
  return rows[0] ? { id: rows[0].id, name: null } : { id: null, name: trimmed };
}

const insertOp = (op: {
  op_id: string;
  action: OutboxAction;
  entity_id: string;
  base_version: number | null;
  payload: Record<string, unknown>;
  effects: Record<string, string>;
}): Statement => ({
  sql: `INSERT INTO outbox (op_id, action, entity_id, base_version, payload, effects, status, created_at)
        VALUES (?, ?, ?, ?, ?, ?, 'pending', ?)`,
  params: [
    op.op_id, op.action, op.entity_id, op.base_version, JSON.stringify(op.payload),
    JSON.stringify(op.effects), nowIso(),
  ],
});

// --- writing: create / update / void ---------------------------------------------------------

export interface Enqueued {
  /** The operation to watch, or null when the change had already been recorded and confirmed. */
  opId: string | null;
}

/**
 * Records a new transaction on this device. `id` is chosen by the caller and stays the
 * transaction's id forever, so calling this twice for one form submission is harmless.
 */
export function enqueueCreate(
  driver: SqlDriver,
  id: string,
  opId: string,
  input: TransactionInput,
): Promise<Enqueued> {
  return serial(driver, async () => {
    if (await loadTxn(driver, id)) {
      const existing = await driver.query<{ op_id: string }>(
        `SELECT op_id FROM outbox WHERE entity_id = ? AND action = 'create'`,
        [id],
      );
      return { opId: existing[0]?.op_id ?? null };
    }

    const amount = normaliseAmount(input.amount);
    const currency = await accountCurrency(driver, input.account_id, "account_id");
    if (input.counter_account_id) await accountCurrency(driver, input.counter_account_id, "counter_account_id");

    let categoryId = input.category_id ?? null;
    if (!categoryId && input.original_transaction_id) {
      // A refund is filed under the category of the purchase it refunds.
      categoryId = (await loadTxn(driver, input.original_transaction_id))?.category_id ?? null;
    }
    const merchant = await resolveMerchant(driver, input.merchant_name);
    const now = nowIso();

    const row: TxnRow = {
      id,
      transaction_type: input.transaction_type,
      transaction_date: input.transaction_date,
      amount,
      currency,
      account_id: input.account_id,
      counter_account_id: input.counter_account_id ?? null,
      category_id: categoryId,
      adjustment_direction: input.adjustment_direction ?? null,
      merchant_id: merchant.id,
      merchant_name: merchant.name,
      description: input.description ?? "",
      reference: input.reference ?? null,
      notes: input.notes ?? null,
      original_transaction_id: input.original_transaction_id ?? null,
      voided_at: null,
      void_reason: null,
      version: 1,
    };

    // What the server is sent: only what the user chose (it derives the rest, e.g. the currency).
    const payload: Record<string, unknown> = { ...input, amount };
    for (const key of Object.keys(payload)) if (payload[key] === undefined) delete payload[key];

    await driver.batch([
      {
        sql: `INSERT INTO transactions (id, transaction_type, transaction_date, amount, currency, account_id,
                counter_account_id, category_id, adjustment_direction, merchant_id, merchant_name,
                description, reference, notes, original_transaction_id, import_batch_id, voided_at,
                void_reason, version, created_at, updated_at, postings)
              VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, NULL, NULL, NULL, 1, ?, ?, NULL)`,
        params: [
          row.id, row.transaction_type, row.transaction_date, row.amount, row.currency, row.account_id,
          row.counter_account_id, row.category_id, row.adjustment_direction, row.merchant_id,
          row.merchant_name, row.description, row.reference, row.notes, row.original_transaction_id,
          now, now,
        ],
      },
      insertOp({
        op_id: opId, action: "create", entity_id: id, base_version: null, payload,
        effects: involvedEffects(row),
      }),
    ]);
    return { opId };
  });
}

const MUTABLE = [
  "transaction_date", "amount", "account_id", "counter_account_id", "category_id", "description",
  "reference", "notes", "original_transaction_id", "adjustment_direction",
] as const;

/**
 * Edits a transaction on this device. `expectedVersion` is the version the user's form was
 * opened on: if the device's copy has moved on since (a sync brought someone else's edit), the
 * save is refused instead of silently overwriting that edit.
 */
export function enqueueUpdate(
  driver: SqlDriver,
  id: string,
  opId: string,
  changes: TransactionChanges,
  expectedVersion?: number,
): Promise<Enqueued> {
  return serial(driver, async () => {
    const before = await loadTxn(driver, id);
    if (!before) throw new ApiError(404, "NOT_FOUND", "Transaction not found");
    if (before.voided_at) throw new ApiError(409, "TRANSACTION_VOIDED", "This transaction was voided.");
    if (expectedVersion !== undefined && before.version !== expectedVersion) {
      throw new ApiError(409, "STALE_WRITE", "This transaction changed. Close and reopen it to see the latest version.");
    }

    const wire: Record<string, unknown> = {};
    const after: TxnRow = { ...before };
    for (const key of MUTABLE) {
      if (changes[key] === undefined) continue;
      const value = key === "amount" ? normaliseAmount(String(changes[key])) : changes[key];
      wire[key] = value;
      (after as unknown as Record<string, unknown>)[key] = value;
    }
    if (changes.merchant_name !== undefined) {
      const merchant = await resolveMerchant(driver, changes.merchant_name);
      wire.merchant_name = changes.merchant_name?.trim() || null;
      after.merchant_id = merchant.id;
      after.merchant_name = merchant.name;
    }
    if (after.account_id !== before.account_id) {
      after.currency = await accountCurrency(driver, after.account_id, "account_id");
    }
    if (after.counter_account_id && after.counter_account_id !== before.counter_account_id) {
      await accountCurrency(driver, after.counter_account_id, "counter_account_id");
    }

    const delta = effectDelta(involvedEffects(before), involvedEffects(after));
    const statements: Statement[] = [
      {
        sql: `UPDATE transactions SET transaction_date = ?, amount = ?, currency = ?, account_id = ?,
                counter_account_id = ?, category_id = ?, adjustment_direction = ?, merchant_id = ?,
                merchant_name = ?, description = ?, reference = ?, notes = ?,
                original_transaction_id = ?, updated_at = ?
              WHERE id = ?`,
        params: [
          after.transaction_date, after.amount, after.currency, after.account_id, after.counter_account_id,
          after.category_id, after.adjustment_direction, after.merchant_id, after.merchant_name,
          after.description, after.reference, after.notes, after.original_transaction_id, nowIso(), id,
        ],
      },
    ];

    // Merge into the operation that has not been sent yet, if there is one.
    const open = (
      await driver.query(
        `SELECT * FROM outbox WHERE entity_id = ? AND status = 'pending' AND action IN ('create', 'update')
         ORDER BY seq DESC LIMIT 1`,
        [id],
      )
    )[0];
    if (open) {
      const op = toOp(open);
      statements.push({
        sql: `UPDATE outbox SET payload = ?, effects = ? WHERE op_id = ? AND status = 'pending'`,
        params: [
          JSON.stringify({ ...op.payload, ...wire }),
          // The operation's effect plus this change (for a create, its effect IS the whole
          // transaction's, so the sum is the effect of the edited transaction).
          JSON.stringify(sumEffects(op.effects, delta)),
          op.op_id,
        ],
      });
      await driver.batch(statements);
      return { opId: op.op_id };
    }

    // The base version is what the device's copy says, unless an earlier operation on this
    // transaction is in flight: that one will change the version, and applyResults re-bases us.
    statements.push(
      insertOp({ op_id: opId, action: "update", entity_id: id, base_version: before.version, payload: wire, effects: delta }),
    );
    await driver.batch(statements);
    return { opId };
  });
}

/** Voids a transaction on this device. Terminal, so it is sent whatever else changed meanwhile. */
export function enqueueVoid(
  driver: SqlDriver,
  id: string,
  opId: string,
  reason: string | null,
): Promise<Enqueued> {
  return serial(driver, async () => {
    const row = await loadTxn(driver, id);
    if (!row) throw new ApiError(404, "NOT_FOUND", "Transaction not found");
    if (row.voided_at) {
      const existing = await driver.query<{ op_id: string }>(
        `SELECT op_id FROM outbox WHERE entity_id = ? AND action = 'void' ORDER BY seq DESC LIMIT 1`,
        [id],
      );
      return { opId: existing[0]?.op_id ?? null };
    }
    const effects: Record<string, string> = {};
    for (const [accountId, amount] of Object.entries(involvedEffects(row))) {
      effects[accountId] = fromUnits(-toUnits(amount)); // taking the transaction back out
    }
    const now = nowIso();
    await driver.batch([
      { sql: `UPDATE transactions SET voided_at = ?, void_reason = ?, updated_at = ? WHERE id = ?`, params: [now, reason, now, id] },
      insertOp({ op_id: opId, action: "void", entity_id: id, base_version: null, payload: { reason }, effects }),
    ]);
    return { opId };
  });
}

// --- sending ---------------------------------------------------------------------------------

/** The next operations to send, in the order the user made them. They are marked as in flight. */
export function takeBatch(driver: SqlDriver, limit = 100): Promise<OutboxOp[]> {
  return serial(driver, async () => {
    const seqs = (
      await driver.query<{ seq: number }>(`SELECT seq FROM outbox WHERE status = 'pending' ORDER BY seq LIMIT ?`, [limit])
    ).map((r) => Number(r.seq));
    if (seqs.length === 0) return [];
    const marks = seqs.map(() => "?").join(", ");
    await driver.run(`UPDATE outbox SET status = 'sending' WHERE seq IN (${marks})`, seqs);
    // Read back only what was just taken - never an older in-flight batch.
    const rows = await driver.query(`SELECT * FROM outbox WHERE seq IN (${marks}) ORDER BY seq`, seqs);
    return rows.map(toOp);
  });
}

/** Puts operations back in the queue (the request never got an answer). */
export function releaseBatch(driver: SqlDriver, ops: readonly OutboxOp[]): Promise<void> {
  return serial(driver, async () => {
    if (ops.length === 0) return;
    await driver.run(
      `UPDATE outbox SET status = 'pending' WHERE status = 'sending' AND op_id IN (${ops.map(() => "?").join(", ")})`,
      ops.map((o) => o.op_id),
    );
  });
}

/**
 * After the app was closed or crashed mid-request: anything marked in flight is queued again.
 * Safe, because the server remembers every op_id and answers a repeat with the original outcome.
 */
export function resetInterrupted(driver: SqlDriver): Promise<void> {
  return serial(driver, () => driver.run(`UPDATE outbox SET status = 'pending' WHERE status = 'sending'`));
}

export function toWire(op: OutboxOp): WireOp {
  const base = { op_id: op.op_id, entity: "transaction", id: op.entity_id, action: op.action };
  if (op.action === "create") return { ...base, data: op.payload };
  if (op.action === "update") return { ...base, base_version: Math.max(1, op.base_version ?? 1), data: op.payload };
  return { ...base, reason: (op.payload.reason as string | null | undefined) ?? null };
}

const UNSENT_OTHERS = `EXISTS (SELECT 1 FROM outbox WHERE entity_id = ? AND status IN ('pending', 'sending'))`;

function statementsForResult(op: OutboxOp, result: PushResult, head: number): Statement[] {
  const out: Statement[] = [];
  const server = result.server;

  if (result.status === "applied" || result.status === "already_applied") {
    out.push({
      sql: `UPDATE outbox SET status = 'acked', acked_head = ?, error_code = NULL, error_message = NULL,
              error_fields = NULL, server = NULL WHERE op_id = ?`,
      params: [head, op.op_id],
    });
    if (server) {
      // Adopt the server's copy - unless the user has already edited it again (then only the
      // version is tracked, and their later operation is sent on top of it).
      out.push(upsert("transaction", server));
      const version = Number(server.version);
      out.push({
        sql: `UPDATE transactions SET version = ? WHERE id = ? AND ${UNSENT_OTHERS}`,
        params: [version, op.entity_id, op.entity_id],
      });
      // Later edits were made on top of this operation's result. Re-base them - but only when
      // the server is exactly where this operation leaves it; if anyone else changed it too,
      // the later edit must still be checked (and can still conflict).
      const expected = op.action === "create" ? 1 : (op.base_version ?? 0) + 1;
      if (op.action !== "void" && version === expected) {
        out.push({
          sql: `UPDATE outbox SET base_version = ?
                 WHERE entity_id = ? AND status = 'pending' AND action = 'update' AND seq > ?`,
          params: [version, op.entity_id, op.seq],
        });
      }
    }
    return out;
  }

  // conflict / rejected: the server wins. The user's change stays in the outbox for review.
  out.push({
    sql: `UPDATE outbox SET status = ?, error_code = ?, error_message = ?, error_fields = ?, server = ?
           WHERE op_id = ?`,
    params: [
      result.status,
      result.error?.code ?? "REJECTED",
      result.error?.message ?? "The server did not accept this change.",
      JSON.stringify(result.error?.fields ?? {}),
      server ? JSON.stringify(server) : null,
      op.op_id,
    ],
  });
  // Later, unsent edits were built on this one: they cannot stand either. (A void after a failed
  // create cannot either - there is nothing on the server to void.)
  out.push({
    sql: `UPDATE outbox SET status = 'rejected', error_code = 'PREVIOUS_CHANGE_FAILED',
            error_message = 'An earlier change to this transaction was not accepted, so this one was not sent.',
            error_fields = NULL, server = ?
           WHERE entity_id = ? AND status = 'pending' AND seq > ? AND (action = 'update' OR ? = 1)`,
    params: [server ? JSON.stringify(server) : null, op.entity_id, op.seq, op.action === "create" ? 1 : 0],
  });
  if (server) {
    out.push(upsert("transaction", server));
  } else {
    // The server has no such transaction: drop the local copy that assumed it would.
    out.push({
      sql: `DELETE FROM transactions WHERE id = ? AND NOT ${UNSENT_OTHERS}`,
      params: [op.entity_id, op.entity_id],
    });
  }
  return out;
}

/** Records the server's answers, all in one atomic commit. */
export function applyResults(
  driver: SqlDriver,
  sent: readonly OutboxOp[],
  response: PushResponse,
): Promise<void> {
  return serial(driver, async () => {
    if (response.results.length !== sent.length) {
      throw new Error(`the server answered ${response.results.length} of ${sent.length} operations`);
    }
    const statements: Statement[] = [];
    sent.forEach((op, i) => {
      const result = response.results[i]!;
      if (result.op_id !== op.op_id) throw new Error("the server's answers do not match the operations sent");
      statements.push(...statementsForResult(op, result, response.head));
    });
    await driver.batch(statements);
  });
}

// --- reading ---------------------------------------------------------------------------------

export interface OutboxSummary {
  /** Changes not yet confirmed by the server. */
  waiting: number;
  /** Changes the server refused, which the user has to look at. */
  attention: number;
}

export async function outboxSummary(driver: SqlDriver): Promise<OutboxSummary> {
  const rows = await driver.query<{ waiting: number; attention: number }>(
    `SELECT COALESCE(SUM(status IN ('pending', 'sending')), 0) AS waiting,
            COALESCE(SUM(status IN ('conflict', 'rejected')), 0) AS attention
       FROM outbox`,
  );
  return { waiting: Number(rows[0]?.waiting ?? 0), attention: Number(rows[0]?.attention ?? 0) };
}

export async function getOp(driver: SqlDriver, opId: string): Promise<OutboxOp | null> {
  const rows = await driver.query(`SELECT * FROM outbox WHERE op_id = ?`, [opId]);
  return rows[0] ? toOp(rows[0]) : null;
}

/** Changes the server refused, oldest first. */
export async function listAttention(driver: SqlDriver): Promise<OutboxOp[]> {
  const rows = await driver.query(`SELECT * FROM outbox WHERE status IN ('conflict', 'rejected') ORDER BY seq`);
  return rows.map(toOp);
}

/** The user has seen a refused change and let it go. The device already holds the server's copy. */
export function discardOp(driver: SqlDriver, opId: string): Promise<void> {
  return serial(driver, () =>
    driver.run(`DELETE FROM outbox WHERE op_id = ? AND status IN ('conflict', 'rejected')`, [opId]),
  );
}
